import { useQuery } from '@tanstack/vue-query';
import { computed } from 'vue';
import apiService from '@services/base/apiService';
import { type VSidebarIndicator } from '@shared/component/VSidebar/utils';
import { useRouteAdviseurnummer } from './routeAdviseurnummer';

// Response van /content/beheer/intern/tussenpersonen/details/getTellersEnWaarschuwingen.asp
export type AdviseurMenuTellingen = {
  aantalgebruikers: number;
  aantalafdelingen: number;
  aantalvergunningen: number;
  aantalgrootboeken: number;
  aantaldocumenten: number;
  aantalwebmodules: number;
  aantallogs: number;
  aantaldvos: number;
  aantallopendedvos: number;
  aantalmemos: number;
  aantaltpalerts: number;
  ccsBriefpapierIngeregeld: boolean;
  tpLogoCCSIngeregeld: boolean;
  tpLogoSUREnetIngeregeld: boolean;
  dvoWaarschuwing: boolean;
  dvoWaarschuwingtxt: string;
  logoDanger: boolean;
  algemeenDanger: boolean;
  medewerkersDanger: boolean;
  vergunningenDanger: boolean;
  grootboekenDanger: boolean;
};

function indicator(value: VSidebarIndicator): VSidebarIndicator {
  return value;
}

export function useAdviseurMenuTellingen() {
  const { adviseurnummer } = useRouteAdviseurnummer();

  const query = useQuery({
    queryKey: ['adviseur', 'menu-tellingen', adviseurnummer],
    queryFn: async () => {
      const response = await apiService.apiClient.get<AdviseurMenuTellingen>(
        `/content/beheer/intern/tussenpersonen/details/getTellersEnWaarschuwingen.asp?adviseurnummer=${adviseurnummer.value}`,
      );
      return response.data;
    },
    enabled: computed(() => !!adviseurnummer.value),
    staleTime: 60_000,
  });

  // Indicators per adviseur-route-name, voor directe consumptie door de sidebar.
  // Volledige response blijft beschikbaar via `data`.
  const countPerRoute = computed<Record<string, VSidebarIndicator | undefined>>(() => {
    const t = query.data.value;
    if (!t) return {};
    return {
      'adviseur-algemeen': indicator({
        variant: t.algemeenDanger ? 'danger' : 'default',
      }),
      'adviseur-medewerkers': indicator({
        count: t.aantalgebruikers,
        variant: t.medewerkersDanger ? 'danger' : 'default',
      }),
      'adviseur-afdelingen': indicator({ count: t.aantalafdelingen }),
      'adviseur-DVO': indicator({
        count: t.aantaldvos,
        variant: t.dvoWaarschuwing ? 'danger' : 'default',
        tooltip: t.dvoWaarschuwing ? t.dvoWaarschuwingtxt : undefined,
      }),
      'adviseur-vergunningen': indicator({
        count: t.aantalvergunningen,
        variant: t.vergunningenDanger ? 'danger' : 'default',
      }),
      'adviseur-grootboeken': indicator({
        count: t.aantalgrootboeken,
        variant: t.grootboekenDanger ? 'danger' : 'default',
      }),
      'adviseur-log': indicator({ count: t.aantallogs }),
      'adviseur-logo': indicator({
        variant: t.logoDanger ? 'danger' : 'default',
      }),
      'adviseur-dossier': indicator({ count: t.aantaldocumenten }),
      'adviseur-webmodules': indicator({ count: t.aantalwebmodules }),
      'adviseur-memos': indicator({ count: t.aantalmemos }),
      'adviseur-alerts': indicator({ count: t.aantaltpalerts }),
    };
  });

  return Object.assign(query, { countPerRoute });
}
